import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { TabProps } from './tabs.types'
import { ScrollButtons } from './tabs-scroll-buttons/tabs-scroll-buttons'
import { useScrollableTabs } from './tabs-scroll-buttons/useScrollableTabs'

const INDICATOR_HEIGHT = '1px'
export const Z_INDEX = { BACKGROUND: 10, CONTENT: 11, OVERLAY: 12 }

export const StyledTabsWrapper = styled.div<{
  $isContentOverflowing: boolean
  $isFirstTabSticky: boolean
}>`
  ${({ theme, $isContentOverflowing, $isFirstTabSticky }) => css`
    display: flex;
    width: 100%;
    position: relative;
    border-bottom: ${INDICATOR_HEIGHT} solid ${theme.colors.gray90};

    ${$isContentOverflowing &&
    css`
      overflow: hidden;
      overflow-x: auto;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
    `}

    ${$isContentOverflowing &&
    $isFirstTabSticky &&
    css`
      > :first-child {
        position: sticky;
        background: #fff;
        z-index: ${Z_INDEX.BACKGROUND};
        left: 0;
        border-right: ${INDICATOR_HEIGHT} solid ${theme.colors.gray90};

        &:not([aria-selected='true']) {
          z-index: ${Z_INDEX.OVERLAY};
        }

        &[aria-selected='true'] {
          border-bottom: ${INDICATOR_HEIGHT} solid ${theme.colors.primary400};
        }
      }
    `}
  `}
`

const StyledTab = styled.div<{ $isActive: boolean; $isDisabled: boolean }>`
  ${({ theme, $isActive, $isDisabled }) => css`
    padding: ${theme.sizes.xs} ${theme.sizes.s};
    font-size: ${theme.fontSizes.bodySmall};
    cursor: default;
    user-select: none;
    white-space: nowrap;
    z-index: 1;
    outline-color: ${theme.colors.primary};
    outline-width: 1px;
    color: ${theme.colors.gray600};
    &:hover {
      color: ${theme.colors.primary};
    }

    &:focus {
      color: ${theme.colors.primary};
    }

    ${$isActive &&
    css`
      color: ${theme.colors.primary};
    `}
    ${$isDisabled &&
    css`
      color: ${theme.colors.gray100};
      pointer-events: none;
    `}
  `}
`

const ActiveIndicator = styled.div`
  ${({ theme }) => css`
    height: ${INDICATOR_HEIGHT};
    background: ${theme.colors.primary};
    position: absolute;
    bottom: -${INDICATOR_HEIGHT};
    left: 0;
    transition: all 0.12s ease;
  `}
`

const HoverIndicator = styled.div`
  background: red;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background: ${({ theme }) => theme.colors.primary100};
  transition: all 0.12s ease;
`

export const Tabs = ({
  items,
  activeTab,
  onChangeTab,
  scrollable = false,
  firstTabSticky = false,
}: TabProps) => {
  const tabListRef = useRef<HTMLDivElement>(null)
  const { isContentOverflowing } = useScrollableTabs(tabListRef, scrollable)
  const [hoverIndicatorPosition, setHoverIndicatorPosition] = useState<{
    opacity: number
    left?: number
    width?: number
  }>({ opacity: 0 })

  const [activeIndicatorPosition, setActiveIndiciatorPosition] = useState<{
    left: number
    width: number
  } | null>(null)

  const removeFirstTabIndicator =
    firstTabSticky && isContentOverflowing && activeTab === items?.[0].key

  useEffect(() => {
    if (tabListRef.current && activeTab) {
      const activeItem = document.getElementById(activeTab)

      if (activeItem) {
        setActiveIndiciatorPosition({
          left: activeItem.offsetLeft,
          width: activeItem.offsetWidth,
        })
      }
    }
  }, [activeTab, items])

  const handleMouseOver = (e: React.MouseEvent<HTMLElement>, id: string) => {
    if (tabListRef.current) {
      const node = e.currentTarget as HTMLElement

      setHoverIndicatorPosition({
        opacity: 1,
        left: node.offsetLeft,
        width: node.offsetWidth,
      })
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.code === 'Enter' || e.code === 'Space') {
      const node = e.target as HTMLElement
      onChangeTab(node.id)
    }
  }

  return (
    <StyledTabsWrapper
      ref={tabListRef}
      role="tablist"
      aria-orientation="horizontal"
      $isContentOverflowing={isContentOverflowing}
      $isFirstTabSticky={firstTabSticky}
    >
      {items.map((tab) => {
        const isActive = tab.key === activeTab

        return (
          <StyledTab
            key={tab.key}
            id={tab.key}
            role="tab"
            tabIndex={0}
            aria-selected={isActive}
            $isActive={isActive}
            $isDisabled={tab.disabled || false}
            onMouseOver={(e) => handleMouseOver(e, tab.key)}
            onMouseLeave={() =>
              setHoverIndicatorPosition((p) => ({ ...p, opacity: 0 }))
            }
            onKeyDown={handleKeyDown}
            onClick={() => onChangeTab(tab.key)}
            data-testid={`tab-${tab.key}`}
          >
            {tab.label}
          </StyledTab>
        )
      })}

      <HoverIndicator style={{ ...hoverIndicatorPosition }} />

      {activeIndicatorPosition && !removeFirstTabIndicator ? (
        <ActiveIndicator
          style={{
            ...activeIndicatorPosition,
            bottom: isContentOverflowing ? 0 : undefined,
            zIndex: Z_INDEX.CONTENT,
          }}
        />
      ) : null}

      {isContentOverflowing && <ScrollButtons containerRef={tabListRef} />}
    </StyledTabsWrapper>
  )
}
